import { v4 as uuid } from "uuid";
import { isProblem, type Problem } from "./problem.js";

// Bug #15 (T18 enact): in dev, Vite's same-origin proxy at /v1/* routes to
// localhost:4000, so relative paths work fine. In production the admin SPA is
// served from admin.wmart.uk and the api from api.wmart.uk — different
// subdomains. VITE_API_URL is baked into the bundle at build time by
// Cloudflare Pages (set in `cloudflare_pages_project.admin.deployment_configs.production.env_vars`)
// and prefixes the absolute origin so fetch hits api.wmart.uk directly with
// credentials:include. Cookies still ship cross-subdomain because
// admin.wmart.uk and api.wmart.uk share `wmart.uk` as eTLD+1, making them
// same-site for SameSite=Lax purposes (the session cookie's host scope is
// api.wmart.uk and that's exactly where the fetch is going).
//
// Empty string default preserves dev behavior (relative paths through Vite proxy).
const API_BASE: string = import.meta.env.VITE_API_URL ?? "";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly problem: Problem | null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Override the auto-generated Idempotency-Key (rare). */
  idempotencyKey?: string;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {
    accept: "application/json",
  };
  if (opts.body !== undefined && !(opts.body instanceof FormData)) {
    headers["content-type"] = "application/json";
  }
  if (method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE") {
    headers["idempotency-key"] = opts.idempotencyKey ?? uuid();
  }

  const init: RequestInit = {
    method,
    credentials: "include",
    headers,
  };
  if (opts.body !== undefined) {
    init.body =
      opts.body instanceof FormData
        ? opts.body
        : JSON.stringify(opts.body);
    if (opts.body instanceof FormData) {
      delete headers["content-type"];
    }
  }

  const res = await fetch(`${API_BASE}${path}`, init);
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // Non-JSON error
    }
    if (isProblem(body)) {
      throw new ApiError(body.title, res.status, body);
    }
    throw new ApiError(`HTTP ${res.status}`, res.status, null);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    request<T>(path, { method: "POST", body, ...opts }),
  patch: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    request<T>(path, { method: "PATCH", body, ...opts }),
  put: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    request<T>(path, { method: "PUT", body, ...opts }),
  delete: <T = void>(path: string, opts?: RequestOpts) =>
    request<T>(path, { method: "DELETE", ...opts }),
  upload: <T>(path: string, form: FormData, opts?: RequestOpts) =>
    request<T>(path, { method: "POST", body: form, ...opts }),
};
